// Dynamic feature component registry using Vite's import.meta.glob
// Each feature component should export a default React component
// File naming convention: <featureId>.tsx (match menuData ids)

import React from 'react';

// Lazy feature module loaders — each entry is a dynamic importer, not a pre-loaded module.
// Vite will code-split every .tsx into its own chunk, loaded on demand.
const modules = import.meta.glob('./**/*.tsx') as Record<string, () => Promise<{ default: React.ComponentType<any> }>>;

// Cache React.lazy components by feature id to avoid re-creating them on re-renders.
const componentCache = new Map<string, React.ComponentType<unknown>>();

export function loadFeatureComponent(id: string): React.ComponentType<unknown> | null {
  // Handle different naming conventions
  let path = `./${id}.tsx`;

  // Handle dashboard folder structure: dashboard-ComponentName -> ./dashboard/ComponentName.tsx
  if (id.startsWith('dashboard-')) {
    const componentName = id.replace('dashboard-', '');
    path = `./dashboard/${componentName}.tsx`;
  }
  // Handle netzoom folder structure: netzoom-ComponentName -> ./netzoom/ComponentName.tsx
  if (id.startsWith('netzoom-')) {
    const componentName = id.replace('netzoom-', '');
    path = `./netzoom/${componentName}.tsx`;
  }

  // Handle emtools folder structure: emtools-ComponentName -> ./emtools/ComponentName.tsx
  if (id.startsWith('emtools-')) {
    const componentName = id.replace('emtools-', '');
    path = `./emtools/${componentName}.tsx`;
  }
  
  // Handle email folder structure: email-ComponentName -> ./email/ComponentName.tsx
  if (id.startsWith('email-')) {
    const componentName = id.replace('email-', '');//make sure component name matches (case sensitive)
    path = `./email/${componentName}.tsx`;
    // console.log('Yadav email- prefixed matched- [loadFeatureComponent] Email path resolved to:', path);  
  }

    // Handle twilio folder structure: twilio-ComponentName -> ./twilio/ComponentName.tsx
  if (id.startsWith('twilio-')) {
    const componentName = id.replace('twilio-', '');
    path = `./twilio/${componentName}.tsx`;
  }

    // Handle powerbi folder structure: powerbi-ComponentName -> ./powerbi/ComponentName.tsx
  if (id.startsWith('powerbi-')) {
    const componentName = id.replace('powerbi-', '');
    path = `./powerbi/${componentName}.tsx`;
  }

      // Handle powerbi folder structure: powerbi-ComponentName -> ./powerbi/ComponentName.tsx
  if (id.startsWith('aimcp-')) {
    const componentName = id.replace('aimcp-', '');
    path = `./aimcp/${componentName}.tsx`;
  }

  // Handle deliverylog folder structure: deliverylog-ComponentName -> ./deliverylog/ComponentName.tsx
  if (id.startsWith('deliverylog-')) {
    const componentName = id.replace('deliverylog-', '');//make sure component name matches (case sensitive)
    path = `./deliverylog/${componentName}.tsx`;
    // console.log('Yadav email- prefixed matched- [loadFeatureComponent] Email path resolved to:', path);  
  }

  // Return cached component if already created.
  if (componentCache.has(id)) return componentCache.get(id)!;

  const loader = modules[path];
  if (!loader)
  {
    // console.log('Yadav- [loadFeatureComponent] Module NOT found. Checking if path exists in modules...');
    const exactMatch = Object.keys(modules).find(key => key.includes('EmailClient'));
    if (exactMatch) {
      // console.log('Yadav- [loadFeatureComponent] Found similar path:', exactMatch);
    }
    // Return a placeholder component for missing features (not cached — fine to recreate)
    return () => React.createElement('div', {
      className: 'p-6 bg-yellow-50 border border-yellow-200 rounded-lg'
    }, [
      React.createElement('h3', {
        key: 'title',
        className: 'text-lg font-semibold text-yellow-800 mb-2'
      }, `${id} - Component Not Implemented`),
      React.createElement('p', {
        key: 'message',
        className: 'text-yellow-700 mb-4'
      }, `The component for feature "${id}" has not been implemented yet.`),
      React.createElement('div', {
        key: 'info',
        className: 'text-sm text-yellow-600 space-y-1'
      }, [
        React.createElement('p', { key: 'path' }, `Expected path: ${path}`),
        React.createElement('p', { key: 'status' }, 'Status: Placeholder - Ready for development')
      ])
    ]);
  }
  
  // Wrap loader in React.lazy, normalising named-export-only modules to { default }.
  const lazyComponent = React.lazy(async () => {
    const mod = await loader();
    if (mod.default) return { default: mod.default };
    // Support named-export-only modules by hoisting the first export as default.
    const firstExport = Object.values(mod)[0] as React.ComponentType<any>;
    return { default: firstExport };
  });

  componentCache.set(id, lazyComponent);
  return lazyComponent;
}
